import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { addDefaultOutlineEffect } from '@/styles/shimmer';

interface ScrollToContentOptions {
  offset?: number;
  behavior?: ScrollBehavior;
  // max number of scroll attempts
  maxRetries?: number;
  // delay after each scroll attempt
  retryDelay?: number;
}


const scrollToTargetElement = (
  targetElement: HTMLElement, 
  offset: number, 
  behavior: ScrollBehavior,
) => {
  const elementTop = targetElement.getBoundingClientRect().top + window.pageYOffset;
  const offsetPosition = elementTop - offset;

  window.scrollTo({
    top: offsetPosition,
    behavior
  });

  return offsetPosition;
};
/**
 * Custom hook to scroll to the target element based on the contentId parameter in the URL.
 */
export const useScrollToContent = (options: ScrollToContentOptions = {}) => {
  const router = useRouter();
  // set default values for the options
  const {
    offset = 50,
    behavior = 'auto',
    maxRetries = 10,
    retryDelay = 500,
  } = options;

  // Track if component has mounted (client-side only)
  const [hasMounted, setHasMounted] = useState(false);
  
  // State for managing spinner visibility
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!hasMounted || typeof window === 'undefined') return;

    let retryCount = 0;
    let isCompleted = false;

    const scrollToElement = () => {
      const { contentid } = router.query as { contentid: string };

      if (!contentid) return;

      // Start showing the spinner
      setIsScrolling(true);

      const focusTargetElement = (element: HTMLElement) => {
        const hadTabIndex = element.hasAttribute('tabindex');
        if (!hadTabIndex) {
          // Use tabindex=0 so the element participates in the natural tab order
          element.setAttribute('tabindex', '0');
        }
        // Prevent any focus-induced scroll jump
        element.focus({ preventScroll: true });
      };

      const waitForScrollCompletion = (targetPosition: number) => {
        let rafId: number | null = null;
        let timeoutId: NodeJS.Timeout | null = null;
        
        const checkScrollPosition = () => {
          const currentPosition = window.pageYOffset;
          const tolerance = 5; // 5px tolerance
          // Check if we've reached the target position
          if (Math.abs(currentPosition - targetPosition) <= tolerance) {
            
            // Final adjustment: re-find element and ensure it's still positioned correctly after the layout shift due to lazy loading
            const targetElement = document.querySelector(`[id="${contentid}"]`) as HTMLElement;
            if (targetElement) {
              scrollToTargetElement(targetElement, offset, 'auto');
              // Ensure keyboard users land on the section
              focusTargetElement(targetElement);
            }
            
            setIsScrolling(false);
            return;
          }
          
          // Continue checking
          rafId = requestAnimationFrame(checkScrollPosition);
        };
        
        // Start checking on next frame
        rafId = requestAnimationFrame(checkScrollPosition);
        
        // Safety timeout to prevent infinite checking
        timeoutId = setTimeout(() => {
          if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
          setIsScrolling(false);
        }, 3000); // 3 second timeout
      };

      const attemptScroll = (): boolean => {
        if (isCompleted) return true;
        
        const targetElement = document.querySelector(`[id="${contentid}"]`) as HTMLElement;
        
        if (targetElement) {
          // Found it! Scroll immediately using the helper function
          const offsetPosition = scrollToTargetElement(targetElement, offset, behavior);

          // Ensure default focus outline is visible for accessibility
          addDefaultOutlineEffect(targetElement);

          isCompleted = true;
                  
          // Wait for scroll animation to complete before hiding spinner
          waitForScrollCompletion(offsetPosition);
          
          return true;
        }

        // Not found - try scrolling to trigger lazy load
        if (retryCount < maxRetries) {
          const currentScroll = window.pageYOffset;
          const documentHeight = document.documentElement.scrollHeight;
          const windowHeight = window.innerHeight;

          const nextScrollPosition = Math.min(
            currentScroll + windowHeight,
            documentHeight - windowHeight
          );

          window.scrollTo({
            top: nextScrollPosition,
            behavior: 'auto'
          });

          retryCount++;

          setTimeout(() => {
            if (!isCompleted) {
              attemptScroll();
            }
          }, retryDelay);

          return false;
        }
        // Hide the spinner after retries are exhausted
        setIsScrolling(false);

        // If element is not found after all retries, scroll to top of the page
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });

        return false;
      };

      attemptScroll();
    };

    // Start immediately - no delay
    scrollToElement();

  }, [hasMounted, router.asPath, offset, behavior, maxRetries, retryDelay]);

  return { isScrolling };
};
